import PropTypes from 'prop-types';
import {useCallback, useEffect, useState} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import PencilIcon from '../../../../svg/icons/pencil.svg';
import MessageType from '../../../enums/MessageType';
import PlusIcon from '../../../UI/PlusIcon/PlusIcon';
import SelectableContainer from '../../../UI/SelectableContainer/SelectableContainer';
import {setGoogleMapsInitStatus} from '../../actions/delivery-section-actions';
import {activateAddCollectionPoint, showPudoMap} from '../../actions/pudo-actions';
import Translation from '../../lang/Translation';
import canEditPudoAddress from '../../selectors/canEditPudoAddress';
import {isClickAndCollectButtonLayout} from '../../selectors/clickAndCollectButtonSelectors';
import {getSelectedPudoAddress} from '../../selectors/PudoSelectors';
import {getSiteCode} from '../../selectors/selectors';
import shouldUseAddressTypeButtons from '../../selectors/shouldUseAddressTypeButtons';
import V2Components from '../../v2-components';
import Message from '../ui/Message/Message';
import useStyles from './Pudo.styles';
import Spinner from '../ui/Spinner';
import PudoSavedAddresses from './PudoSavedAddresses/PudoSavedAddresses';

export function Pudo(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  const [error, setError] = useState(null);
  
  const validate = useCallback(
    () => {
      if (props.hasSelectedAddress) {
        setError(null);
        return {isValid: true};
      }
      
      setError('deliveryOptions.requireData.pudo');
        
      return {
        isValid: false,
        errors: [{
          fieldName: 'pudo.selectedAddress',
          errorCode: 'deliveryOptions.requireData.pudo',
          pageError: 'errors.mainFormError',
        }],
      };
    }, [props.hasSelectedAddress],
  );
  
  useEffect(() => {
    if (props.hasNewClickAndCollectButtonLayout) {
      V2Components.register('Pudo', {validate});
    }
    
    if (error && props.hasSelectedAddress) {
      validate();
    }
    
    return () => {
      if (props.hasNewClickAndCollectButtonLayout) {
        V2Components.clear('Pudo');
      }
    };
  }, [validate, props.hasSelectedAddress]);
  
  return (
    <div className="Pudo">
      <PudoSavedAddresses
        disabled={props.disabled}
      />
  
      {props.useAddressTypeButtons && (
        <div className={classes.pudoButtonLabel} data-test="Pudo__pudoButtonLabel">
          <Translation optionalExtension={props.siteCode}>pudo.addCollectionPointLabel</Translation>
        </div>
      )}
  
      <SelectableContainer
        componentId="Pudo"
        isSelected={false}
        onClick={props.showPudoMap}
        fullWidth
      >
        <div className={classes.pudoContent} data-test="Pudo__content">
          {props.isGoogleMapsLoading && (
            <span className={classes.spinnerWrapper}><Spinner size="extra-small" /></span>
          )}
  
          <span className={classes.icon}>
            {props.canEdit ? <img className={classes.pencilIcon} src={PencilIcon} alt="" /> : <PlusIcon />}
          </span>
  
          <Translation optionalExtension={props.siteCode}>{props.canEdit ? 'pudo.changeCollectionPoint' : 'pudo.addCollectionPoint'}</Translation>
        </div>
      </SelectableContainer>
  
      {error && (
        <div className={classes.hasNoSelectedPudoPointError} data-test="Pudo__hasNoSelectedPudoPointError">
          <Message
            type={MessageType.ERROR}
          >
            <Translation optionalExtension={props.siteCode}>{error}</Translation>
          </Message>
        </div>
      )}
    </div>
  );
}

Pudo.propTypes = {
  activateAddCollectionPoint: PropTypes.func,
  canEdit: PropTypes.bool,
  disabled: PropTypes.bool,
  isGoogleMapsLoading: PropTypes.bool,
  setGoogleMapsInitStatus: PropTypes.func,
  showPudoMap: PropTypes.func,
  siteCode: PropTypes.string.isRequired,
  useAddressTypeButtons: PropTypes.bool.isRequired,
  hasSelectedAddress: PropTypes.bool,
  hasNewClickAndCollectButtonLayout: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    useAddressTypeButtons: shouldUseAddressTypeButtons(state),
    isGoogleMapsLoading: state.delivery.isGoogleMapsLoading,
    canEdit: canEditPudoAddress(state),
    siteCode: getSiteCode(state),
    hasSelectedAddress: !!getSelectedPudoAddress(state),
    hasNewClickAndCollectButtonLayout: isClickAndCollectButtonLayout(state),
  };
}

Pudo.defaultProps = {
  canEdit: true,
};

export default connect(mapStateToProps, {
  activateAddCollectionPoint,
  setGoogleMapsInitStatus,
  showPudoMap,
})(Pudo);
